// The two reads `dashboard/api.ts` does not carry yet — corpus sufficiency
// (US-063) and term detail (US-062). Everything else these screens need
// (`useTopTerms`, `useDocTimeline`, `useVocabularyHealth`) already lives there
// and is imported, not reimplemented.
//
// `dashboard/api.ts` is another agent's path this wave, so its private
// `API_BASE` / `getJSON` helpers could not be exported for reuse and are
// mirrored below. `polish` should collapse the two back into one module.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getKey } from "../../dashboard/api";

const API_BASE =
  (import.meta.env?.VITE_API_BASE as string | undefined) ??
  "http://localhost:8000";

/** Panels are read-only and cheap; a minute of cache is what makes the browser
 *  back button return to the overview without a refetch flash (US-112). */
const STALE_MS = 60_000;

export interface CorpusSufficiency {
  org_slug: string;
  days: number;
  verdict: "sufficient" | "insufficient";
  documents: number;
  terms: number;
  /** Visits in the window. NEVER people — see metricLabels.ts. */
  sessions: number;
  thresholds: { documents: number; terms: number; sessions: number };
  /** One human-readable string per count that fell short. Empty iff sufficient.
   *  The threshold itself lives only in the backend; frame 12 renders these. */
  reasons: string[];
  unexplained_terms: number;
  unexplained_examples: string[];
}

export interface TermHistoryPoint {
  date: string;
  doc_id: string;
  hover_count: number;
  unique_sessions: number;
  median_dwell_ms: number | null;
  abandonment_within_5s: number;
  share_of_section_views: number | null;
}

export interface TermDetail {
  org_slug: string;
  term_id: string;
  days: number;
  term: string | null;
  definition: string | null;
  origin: string | null;
  doc_ids: string[];
  hover_count: number;
  unique_sessions: number;
  median_dwell_ms: number | null;
  abandonment_within_5s: number;
  /** USAGE: `hover_count / unique_sessions`. `null` when no visit opened the
   *  term — no denominator, so no rate. Render NOT_MEASURED, never `0`. */
  lookups_per_session: number | null;
  history: TermHistoryPoint[];
}

/** One side of a Fix-and-Prove comparison. `sessions` counts VISITS — the
 *  backend's `unique_sessions`, renamed here only to shorten it, never to
 *  suggest a person (see metricLabels.ts). */
export interface LedgerWindow {
  start: string;
  end: string;
  days_with_data: number;
  sessions: number;
  hover_count: number;
  /** ENGAGEMENT, reported beside the rate and never folded into the verdict. */
  median_dwell_ms: number | null;
  abandonment_within_5s: number;
  /** USAGE, and what the verdict is computed on: `hover_count / sessions`.
   *  `null` when `sessions` is 0 — an absent denominator, not a measured
   *  zero. `1.0` means every visit opened the term once and moved on. */
  lookups_per_session: number | null;
  /** Every `term_id` that contributed. Length > 1 on the after-side is the
   *  visible proof that the slug join spanned a re-minted id. */
  term_ids: string[];
}

export interface LedgerEntry {
  doc_id: string;
  term: string;
  /** The join key: `term_id` minus its definition hash. The before and after
   *  halves share this and nothing else once a definition has been rewritten. */
  slug: string;
  edited_at: string;
  editor: string;
  old_definition: string;
  new_definition: string;
  before: LedgerWindow;
  after: LedgerWindow;
  after_window_complete: boolean;
  /** `null` iff `verdict === "not_measured"`. Render NOT_MEASURED, never 0%.
   *  Negative is the WIN: readers stopped needing a second look. */
  lookups_delta_pct: number | null;
  verdict: "improved" | "worsened" | "unchanged" | "not_measured";
  not_measured_reason: string | null;
}

export interface FixAndProveLedger {
  org_slug: string;
  days: number;
  window_days: number;
  min_sessions: number;
  /** Rewrites found in the window, before any were judged measurable.
   *  `edits_found === 0` is "nobody edited anything", which is a different
   *  statement from "we could not measure the edits" and reads differently. */
  edits_found: number;
  entries_measured: number;
  entries: LedgerEntry[];
}

/** One section of one document over the window — A4.
 *
 * `sessions_*` count VISITS (see metricLabels.ts). The two shares have
 * different denominators and the names say which: `exit_share` divides by the
 * visits that reached THIS section, `reach_share` by the visits that viewed any
 * section of the document. */
export interface SectionStop {
  section_id: string;
  heading: string | null;
  /** 1-based reading order. `null` when the events know a heading slug the
   *  current document does not — a renamed or deleted section. */
  position: number | null;
  /** `null` when the document's order is unavailable, which is also what costs
   *  every row its verdict: stopping at the end is finishing, not dropping. */
  is_final_section: boolean | null;
  sessions_reached: number;
  sessions_exited: number;
  doc_sessions: number;
  reach_share: number | null;
  /** `null` iff `verdict === "not_measured"`. Render NOT_MEASURED, never 0%. */
  exit_share: number | null;
  verdict: "drop_off" | "read_on" | "ends_here" | "not_measured";
  not_measured_reason: string | null;
}

export interface SectionDropOffMap {
  org_slug: string;
  days: number;
  min_sessions: number;
  /** `null` when the org recorded no section views at all in the window. */
  doc_id: string | null;
  doc_title: string | null;
  auto_selected: boolean;
  sections_ordered: boolean;
  sections_measured: number;
  sections: SectionStop[];
}

// ---- A2, the term look-up leaderboard with trend ---------------------------

/** One day of one term's sparkline.
 *
 * `lookups_per_session` is `null` on a day no visit opened the term. That is a
 * GAP in the line, never a point on the axis: a rate with no denominator does
 * not exist, and plotting it at zero claims "nobody opened this" when nothing
 * was measured at all. */
export interface TrendPoint {
  date: string;
  /** VISITS, never people (see metricLabels.ts). */
  sessions: number;
  lookups_per_session: number | null;
}

export interface TrendWindow {
  start: string;
  end: string;
  sessions: number;
  hover_count: number;
  abandonment_within_5s: number;
  /** ENGAGEMENT, reported beside the rate and never folded into it. */
  median_dwell_ms: number | null;
  /** USAGE, and the rank: `hover_count / sessions`. `null` when no visit
   *  opened the term — absent, not zero. `1.0` means opened once per visit. */
  lookups_per_session: number | null;
  /** Share of the visits that reached this term's section and then opened it.
   *  Reach, not rank — the ranking is per visit. `null` on any document with no
   *  recorded section views. */
  share_of_section_views: number | null;
}

export interface TermTrend {
  doc_id: string;
  term_id: string;
  current: TrendWindow;
  previous: TrendWindow;
  /** `null` iff `direction === "not_measured"`. Render NOT_MEASURED, never 0%. */
  delta_pct: number | null;
  direction: "improved" | "worsened" | "flat" | "not_measured";
  not_measured_reason: string | null;
  series: TrendPoint[];
}

export interface LookupTrend {
  org_slug: string;
  days: number;
  min_sessions: number;
  /** Terms with rollup rows, before measurability was considered. The gap to
   *  `terms_measured` is the size of the unmeasured tail and is stated. */
  terms_found: number;
  terms_measured: number;
  terms: TermTrend[];
}

// ---- B2, the impact radius -------------------------------------------------

export interface RadiusRow {
  term: string;
  entity_id: string;
  doc_ids: string[];
  /** `"<doc_id>:<term_id>"` per member — the raw Pass 2 provenance the
   *  incidence matrix keys on. NEVER split this and rejoin on the tail:
   *  `term_id` is document-local, so a join on it alone collapses 217 distinct
   *  cells into the 12 distinct ids that exist. */
  member_refs: string[];
  doc_count: number;
  /** Min pair-cosine of the cluster behind the concept. `null` is UNKNOWN and
   *  must never be read as 0 — B1 ranks on `(1 - confidence) x doc_count`. */
  confidence: number | null;
}

export interface ImpactRadiusReport {
  org_slug: string;
  corpus_id: string;
  /** `false` means Pass 2 reconciliation has never produced a glossary for this
   *  corpus — a different claim from "nothing spans two documents", and the one
   *  the seeded corpus is actually in. */
  reconciled: boolean;
  concepts_found: number;
  concepts_spanning: number;
  symbols_skipped: number;
  orphans_skipped: number;
  rows: RadiusRow[];
}

// ---- B3, prerequisite debt -------------------------------------------------

/** One concept on the document, and what it assumes the reader already knows.
 *
 * The two "missing definition" fields are DIFFERENT PROBLEMS and are never
 * summed: `unresolved_prereqs` match no concept anywhere in the corpus (nothing
 * defines them), `off_document_prereqs` are defined in another document (the
 * reader has to leave the page). */
export interface PrereqRow {
  term: string;
  entity_id: string;
  /** Longest chain of prerequisites beneath this concept. `null` iff
   *  `verdict === "not_measured"`. A `0` is a real answer meaning "assumes
   *  nothing" — never render it as absent. */
  depth: number | null;
  /** True when the chain was still descending at `depth_cap`. Render "4 or
   *  more"; the true depth is unknown and larger. */
  depth_capped: boolean;
  verdict: "measured" | "not_measured";
  not_measured_reason: string | null;
  prereq_terms: string[];
  unresolved_prereqs: string[];
  off_document_prereqs: string[];
}

export interface PrereqDebtReport {
  org_slug: string;
  corpus_id: string;
  /** `false` means Pass 2 reconciliation has never produced a glossary — a
   *  different claim from "nothing assumes prior knowledge". */
  reconciled: boolean;
  doc_id: string | null;
  auto_selected: boolean;
  depth_cap: number;
  concepts_found: number;
  max_depth: number | null;
  max_depth_capped: boolean;
  concepts_with_prereqs: number;
  unresolved_total: number;
  off_document_total: number;
  cycles_found: number;
  rows: PrereqRow[];
}

// ---- B4, the term coupling graph -------------------------------------------

/** Two terms one visit looked up, and how much more often than chance.
 *
 * `sessions_both` counts VISITS — two look-ups in ONE TAB. Not one person, and
 * never across visits (see metricLabels.ts). The pairing is UNORDERED: the
 * backend keys `term_a < term_b` and stores an absolute gap, so no copy here
 * may say one term was looked up before the other. */
export interface CoupledPair {
  term_a: string;
  term_b: string;
  /** Display strings from the published glossary. `null` when the rollup knows
   *  a `term_id` the current document does not; fall back to the id. */
  name_a: string | null;
  name_b: string | null;
  sessions_both: number;
  sessions_a: number;
  sessions_b: number;
  doc_sessions: number;
  days_together: number;
  /** What chance alone would produce. `null` iff `lift` is. "8x" is not a claim
   *  until the number it is eight times of is on screen beside it. */
  expected_together: number | null;
  /** `null` iff `verdict === "not_measured"`. NEVER 0 — a lift of 1.0 already
   *  means "no association", so 0 would be an impossible reading, not a weak
   *  one. */
  lift: number | null;
  /** Typical elapsed time between the two look-ups. NOT a dwell — it says how
   *  close together they were, never how long either was read. */
  median_gap_ms: number | null;
  verdict: "coupled" | "independent" | "not_measured";
  not_measured_reason: string | null;
}

export interface TermCouplingReport {
  org_slug: string;
  days: number;
  min_pair_sessions: number;
  min_doc_sessions: number;
  doc_id: string | null;
  doc_title: string | null;
  auto_selected: boolean;
  doc_sessions: number;
  /** Distinct pairs observed, INCLUDING the suppressed ones. */
  pairs_found: number;
  pairs_measured: number;
  /** Pairs omitted from `pairs` entirely because too few visits opened both.
   *  The anonymity gate, applied in the backend before a row is built — a pair
   *  opened by three visits is close to describing one visit's reading, and a
   *  term-hover sequence is a near-unique behavioural fingerprint. Only this
   *  aggregate count reaches the wire; none of the pairs behind it do. */
  pairs_suppressed: number;
  pairs: CoupledPair[];
}

// ---- B5, the Defined x Used quadrant ---------------------------------------

export interface QuadrantTerm {
  term_id: string;
  /** `null` in the `used_undefined` corner — the document no longer names it. */
  term: string | null;
  quadrant: "defined_used" | "defined_unused" | "used_undefined" | "not_measured";
  hover_count: number;
  /** VISITS, never people. */
  sessions: number;
  /** Corpus-TEXT signal: used at most once in its own prose. Not behavioural,
   *  and never mixed into the hover counts. */
  is_singleton: boolean;
  not_measured_reason: string | null;
}

export interface DefinedUsedQuadrant {
  org_slug: string;
  days: number;
  min_sessions: number;
  doc_id: string | null;
  doc_title: string | null;
  auto_selected: boolean;
  /** `false` is a document with no published glossary, in which case nothing is
   *  classified — "used but undefined" would otherwise swallow the document. */
  glossary_published: boolean;
  doc_sessions: number;
  terms_defined: number;
  defined_used: number;
  defined_unused: number;
  used_undefined: number;
  not_measured: number;
  /** `defined_unused / terms_defined`. `null` below the traffic floor — never
   *  0, which would claim a clean glossary on a page nobody read. */
  unused_share: number | null;
  /** ALWAYS false, permanently. A hover fires only on a marked term, so demand
   *  for a word the glossary never covered emits no event at all. Nothing in
   *  this payload estimates it and no panel may imply otherwise. */
  undefined_demand_measurable: boolean;
  undefined_demand_reason: string;
  /** Defined terms whose own prose uses them at most once — a corpus-text lead
   *  toward that unmeasurable corner, never a count of readers. */
  singletons_defined: number;
  terms: QuadrantTerm[];
}

// ---- C4, the within-session learning curve ---------------------------------

/** One term's first-look-versus-later-looks contrast, inside single visits.
 *
 * `re_encounters` counts term RE-ENCOUNTERS — one visit that re-opened three
 * terms contributes three — never visits and never people. A visit is one
 * browser tab; nothing here spans two of them and nothing ever will. */
export interface TermLearning {
  term_id: string;
  /** `null` when the rollup knows an id the published document no longer does;
   *  fall back to the id rather than dropping the row. */
  term: string | null;
  re_encounters: number;
  faster_later: number;
  /** `null` iff `verdict === "not_measured"`. NEVER conflated with `0`, which
   *  is a real and alarming reading — nobody ever got quicker. */
  faster_share: number | null;
  /** Typical first and later dwell. `null` when no day cleared the backend's
   *  write-time anonymity floor, which is common on low-traffic documents. The
   *  share is then the only answer, and it is still an answer. */
  first_dwell_ms: number | null;
  later_dwell_ms: number | null;
  days_measured: number;
  verdict: "faster" | "no_change" | "not_measured";
  not_measured_reason: string | null;
}

export interface LearningCurveReport {
  org_slug: string;
  days: number;
  min_re_encounters: number;
  doc_id: string | null;
  doc_title: string | null;
  auto_selected: boolean;
  /** `false` means the instrument was silent, NOT that the curve is flat. The
   *  whole view is built on `term_dwell`, which nothing emitted until
   *  2026-08-16, so every window reaching into the past lands here. Render
   *  `not_measured_reason`; never draw a curve at zero. */
  measured: boolean;
  not_measured_reason: string | null;
  re_encounters: number;
  faster_later: number;
  faster_share: number | null;
  first_dwell_ms: number | null;
  later_dwell_ms: number | null;
  verdict: "faster" | "no_change" | "not_measured";
  terms_found: number;
  terms_measured: number;
  /** Ordered slowest-learning first: a term readers never get quicker on is a
   *  definition that is not landing, and it is the actionable row. */
  terms: TermLearning[];
}

function authHeaders(): Record<string, string> {
  const key = getKey();
  return key
    ? { Accept: "application/json", "X-mAiSight-Key": key }
    : { Accept: "application/json" };
}

function url(path: string, params: Record<string, string | number>): string {
  const built = new URL(
    `${API_BASE}${path}`,
    typeof window !== "undefined" ? window.location.origin : undefined,
  );
  for (const [name, value] of Object.entries(params)) {
    built.searchParams.set(name, String(value));
  }
  return built.toString();
}

export function useCorpusSufficiency(
  orgSlug: string,
  days: number,
): UseQueryResult<CorpusSufficiency, Error> {
  return useQuery<CorpusSufficiency, Error>({
    queryKey: ["analytics-sufficiency", orgSlug, days],
    staleTime: STALE_MS,
    queryFn: async () => {
      const target = url(`/dashboards/${orgSlug}/sufficiency`, { days });
      const res = await fetch(target, { headers: authHeaders() });
      if (!res.ok) throw new Error(`sufficiency unavailable (${res.status})`);
      return (await res.json()) as CorpusSufficiency;
    },
  });
}

/**
 * A1's ledger. `days` is how far back to look for rewrites; `windowDays` is the
 * half-width of each before/after comparison, so the two are independent and
 * the range chips only move the former.
 */
export function useFixAndProve(
  orgSlug: string,
  days: number,
  windowDays = 14,
): UseQueryResult<FixAndProveLedger, Error> {
  return useQuery<FixAndProveLedger, Error>({
    queryKey: ["analytics-fix-and-prove", orgSlug, days, windowDays],
    staleTime: STALE_MS,
    queryFn: async () => {
      const target = url(`/dashboards/${orgSlug}/fix-and-prove`, {
        days,
        window_days: windowDays,
      });
      const res = await fetch(target, { headers: authHeaders() });
      if (!res.ok) throw new Error(`fix-and-prove unavailable (${res.status})`);
      return (await res.json()) as FixAndProveLedger;
    },
  });
}

/**
 * A4's map. `docId` is omitted by the panel: the backend picks the busiest
 * document in the window and says so via `auto_selected`, which keeps the
 * document choice a single decision made against the data rather than a
 * default hardcoded on the screen.
 */
export function useSectionDropOff(
  orgSlug: string,
  days: number,
  docId?: string,
): UseQueryResult<SectionDropOffMap, Error> {
  return useQuery<SectionDropOffMap, Error>({
    queryKey: ["analytics-section-dropoff", orgSlug, days, docId ?? null],
    staleTime: STALE_MS,
    queryFn: async () => {
      const target = url(
        `/dashboards/${orgSlug}/section-dropoff`,
        docId ? { days, doc_id: docId } : { days },
      );
      const res = await fetch(target, { headers: authHeaders() });
      if (!res.ok) throw new Error(`section drop-off unavailable (${res.status})`);
      return (await res.json()) as SectionDropOffMap;
    },
  });
}

/**
 * A2's leaderboard. Replaces `useTopTerms` on this screen: the same rollup, but
 * ranked on look-ups per visit instead of the raw total, and carrying the
 * previous window plus a per-day series so the panel can show whether a term is
 * getting better rather than only that it is heavily used.
 */
export function useLookupTrend(
  orgSlug: string,
  days: number,
  limit = 8,
): UseQueryResult<LookupTrend, Error> {
  return useQuery<LookupTrend, Error>({
    queryKey: ["analytics-lookup-trend", orgSlug, days, limit],
    staleTime: STALE_MS,
    queryFn: async () => {
      const target = url(`/dashboards/${orgSlug}/lookup-trend`, {
        days,
        limit,
      });
      const res = await fetch(target, { headers: authHeaders() });
      if (!res.ok) throw new Error(`look-up trend unavailable (${res.status})`);
      return (await res.json()) as LookupTrend;
    },
  });
}

/**
 * B2's radius, and the source of the `confidence` B1 ranks on.
 *
 * `corpusId` is required by the backend; one corpus per org is the same
 * assumption `useVocabularyHealth` and `reports/monthly.py` make. A 404 is a
 * corpus this org does not own, which reads here as a permanently absent panel
 * rather than a failed dashboard — the same shape `useVocabularyHealth` uses.
 */
  /** `null` while `useCorpora` is still resolving, and for an org that owns
 *  none. The query stays DISABLED rather than falling back to the org slug:
 *  that fallback is the exact defect this module's corpus-resolution section
 *  describes, and it does not stop being wrong for being brief — it resolves
 *  to a corpus that does not exist, `_owns_corpus` authorizes it anyway, and
 *  the panel renders "Pass 2 has never run" over reconciled data until the
 *  real id arrives. Same shape as `useCorpusGlossary`. */
export function useImpactRadius(
  orgSlug: string,
  corpusId: string | null,
): UseQueryResult<ImpactRadiusReport | null, Error> {
  return useQuery<ImpactRadiusReport | null, Error>({
    queryKey: ["analytics-impact-radius", orgSlug, corpusId],
    staleTime: STALE_MS,
    enabled: corpusId !== null,
    queryFn: async () => {
      const target = url(`/dashboards/${orgSlug}/impact-radius`, {
        corpus_id: corpusId as string,
      });
      const res = await fetch(target, { headers: authHeaders() });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`impact radius unavailable (${res.status})`);
      return (await res.json()) as ImpactRadiusReport;
    },
  });
}

/**
 * B3's prerequisite debt. Cheapest of the Tier B views: `prereq_dag` has been
 * written on every reconcile since Pass 2 shipped and had zero consumers.
 *
 * `corpusId` is required by the backend; one corpus per org is the same
 * assumption `useImpactRadius` makes, and a 404 resolves to `null` for the same
 * reason — a corpus this org does not own is a permanently absent panel, not a
 * failed dashboard.
 */
  /** `null` while `useCorpora` is still resolving, and for an org that owns
 *  none. The query stays DISABLED rather than falling back to the org slug:
 *  that fallback is the exact defect this module's corpus-resolution section
 *  describes, and it does not stop being wrong for being brief — it resolves
 *  to a corpus that does not exist, `_owns_corpus` authorizes it anyway, and
 *  the panel renders "Pass 2 has never run" over reconciled data until the
 *  real id arrives. Same shape as `useCorpusGlossary`. */
export function usePrereqDebt(
  orgSlug: string,
  corpusId: string | null,
): UseQueryResult<PrereqDebtReport | null, Error> {
  return useQuery<PrereqDebtReport | null, Error>({
    queryKey: ["analytics-prereq-debt", orgSlug, corpusId],
    staleTime: STALE_MS,
    enabled: corpusId !== null,
    queryFn: async () => {
      const target = url(`/dashboards/${orgSlug}/prereq-debt`, {
        corpus_id: corpusId as string,
      });
      const res = await fetch(target, { headers: authHeaders() });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`prerequisite debt unavailable (${res.status})`);
      return (await res.json()) as PrereqDebtReport;
    },
  });
}

/**
 * B4's coupling graph — the only panel on this screen computed from behaviour
 * rather than from corpus text.
 *
 * `docId` is omitted by the panel for the reason `useSectionDropOff` gives: the
 * backend picks the document with the most co-hover traffic and says so via
 * `auto_selected`, which keeps that choice one decision made against the data
 * rather than a default hardcoded on a screen.
 */
export function useTermCoupling(
  orgSlug: string,
  days: number,
  docId?: string,
): UseQueryResult<TermCouplingReport, Error> {
  return useQuery<TermCouplingReport, Error>({
    queryKey: ["analytics-term-coupling", orgSlug, days, docId ?? null],
    staleTime: STALE_MS,
    queryFn: async () => {
      const target = url(
        `/dashboards/${orgSlug}/term-coupling`,
        docId ? { days, doc_id: docId } : { days },
      );
      const res = await fetch(target, { headers: authHeaders() });
      if (!res.ok) throw new Error(`term coupling unavailable (${res.status})`);
      return (await res.json()) as TermCouplingReport;
    },
  });
}

/**
 * C4's learning curve — first look versus later looks, inside one visit.
 *
 * Same document-selection contract as `useTermCoupling`. The payload's
 * `measured` flag is the field that matters: `false` is the instrument being
 * silent, not a flat curve, and the panel must render the backend's reason
 * rather than a zero.
 */
export function useLearningCurve(
  orgSlug: string,
  days: number,
  docId?: string,
): UseQueryResult<LearningCurveReport, Error> {
  return useQuery<LearningCurveReport, Error>({
    queryKey: ["analytics-learning-curve", orgSlug, days, docId ?? null],
    staleTime: STALE_MS,
    queryFn: async () => {
      const target = url(
        `/dashboards/${orgSlug}/learning-curve`,
        docId ? { days, doc_id: docId } : { days },
      );
      const res = await fetch(target, { headers: authHeaders() });
      if (!res.ok) throw new Error(`learning curve unavailable (${res.status})`);
      return (await res.json()) as LearningCurveReport;
    },
  });
}

/** B5's quadrant. Same document-selection contract as the two above. */
export function useDefinedUsed(
  orgSlug: string,
  days: number,
  docId?: string,
): UseQueryResult<DefinedUsedQuadrant, Error> {
  return useQuery<DefinedUsedQuadrant, Error>({
    queryKey: ["analytics-defined-used", orgSlug, days, docId ?? null],
    staleTime: STALE_MS,
    queryFn: async () => {
      const target = url(
        `/dashboards/${orgSlug}/defined-used`,
        docId ? { days, doc_id: docId } : { days },
      );
      const res = await fetch(target, { headers: authHeaders() });
      if (!res.ok) throw new Error(`defined vs used unavailable (${res.status})`);
      return (await res.json()) as DefinedUsedQuadrant;
    },
  });
}

/**
 * `docId` is not optional in practice, only in the type.
 *
 * A `term_id` is doc-LOCAL in every fixture and seeded document — `t04` names a
 * different term in each one — so a detail fetched without `doc_id` aggregates
 * a meaningless mixture across documents. Every caller inside these screens
 * carries the `doc` search param through from the row that was clicked.
 *
 * A 404 resolves to `null` rather than throwing: an unknown or cross-org term
 * id is a not-found screen, not an error screen. The backend returns 404 for
 * both cases on purpose, so the two are indistinguishable from here.
 */
export function useTermDetail(
  orgSlug: string,
  termId: string,
  docId: string | undefined,
  days: number,
): UseQueryResult<TermDetail | null, Error> {
  return useQuery<TermDetail | null, Error>({
    queryKey: ["analytics-term", orgSlug, termId, docId ?? null, days],
    staleTime: STALE_MS,
    queryFn: async () => {
      const target = url(
        `/dashboards/${orgSlug}/terms/${encodeURIComponent(termId)}`,
        docId ? { days, doc_id: docId } : { days },
      );
      const res = await fetch(target, { headers: authHeaders() });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`term detail unavailable (${res.status})`);
      return (await res.json()) as TermDetail;
    },
  });
}

/* ---- corpus resolution --------------------------------------------------
 *
 * AN ORG CAN OWN MORE THAN ONE CORPUS, and none of them is necessarily named
 * after the org. `acme` owns three. Before this hook existed, every panel here
 * passed `orgSlug` through as `corpus_id`, which resolved to a corpus that
 * does not exist — authorized by the `_owns_corpus` naming-convention
 * fallback, then answered `reconciled: false` on fully reconciled data. The
 * seed gate never saw it because `scripts/seed/checks.py` iterates the real
 * ids. Resolve here; never assume the slug.
 */

export interface CorpusSummary {
  corpus_id: string;
  /** False = Pass 2 has never run. A different claim from "no concepts". */
  reconciled: boolean;
  concepts: number;
}

export interface CorpusList {
  org_slug: string;
  corpora: CorpusSummary[];
}

export function useCorpora(
  orgSlug: string,
): UseQueryResult<CorpusList, Error> {
  return useQuery<CorpusList, Error>({
    queryKey: ["analytics-corpora", orgSlug],
    staleTime: STALE_MS,
    queryFn: async () => {
      const res = await fetch(url(`/dashboards/${orgSlug}/corpora`, {}), {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`corpus list unavailable (${res.status})`);
      return (await res.json()) as CorpusList;
    },
  });
}

/** One canonical concept and the document terms that merged into it.
 *
 * `is_singleton` is NOT here and never will be — the reconciler drops it when
 * it builds a `CorpusEntity`. "The canonicaliser packed nothing into this" is
 * `member_refs.length === 1`. `surface` is not here either; the canonical
 * surface is `name`.
 */
export interface CorpusEntity {
  entity_id: string;
  name: string;
  kind: string;
  member_refs: string[];
  /** Exact-name clusters come back as 1.0000000000000002 from float error.
   *  Clamp before drawing anything proportional to it. */
  confidence: number | null;
  is_orphan: boolean;
}

export interface CorpusGlossary {
  corpus_id: string;
  reconcile_version: number;
  entities: CorpusEntity[];
  prereq_dag: Record<string, string[]>;
}

/** `null` when Pass 2 has never run for this corpus — a permanently absent
 *  panel rather than a failed dashboard, same as `useImpactRadius`. */
export function useCorpusGlossary(
  orgSlug: string,
  corpusId: string | null,
): UseQueryResult<CorpusGlossary | null, Error> {
  return useQuery<CorpusGlossary | null, Error>({
    queryKey: ["analytics-corpus-glossary", orgSlug, corpusId],
    staleTime: STALE_MS,
    enabled: corpusId !== null,
    queryFn: async () => {
      const target = url(`/dashboards/${orgSlug}/corpus-glossary`, {
        corpus_id: corpusId as string,
      });
      const res = await fetch(target, { headers: authHeaders() });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`corpus glossary unavailable (${res.status})`);
      return (await res.json()) as CorpusGlossary | null;
    },
  });
}

/* ---- the clock (X2) ----------------------------------------------------- */

export type ClockCycle = "day" | "week";
export type ClockGrain = "hour" | "quarter";

export interface ClockBucket {
  slot: number;
  lookups: number;
  sessions: number;
  section_views: number;
}

export interface ClockTermBucket {
  slot: number;
  /** `null` where the slot fell under the disclosure floor. A GAP, never a
   *  zero — the term may well have been opened here; the backend declines to
   *  say. Render nothing, and quote `term_slots_measured` beside it. */
  lookups: number | null;
  sessions: number | null;
}

export interface ClockReport {
  org_slug: string;
  days: number;
  cycle: ClockCycle;
  /** The grain actually used. A `week` cycle always answers `hour`. */
  grain: ClockGrain;
  slots: number;
  buckets: ClockBucket[];
  sessions: number;
  lookups: number;
  /** Busiest bucket over the mean. 1.0 is a perfectly even cycle.
   *  `null` iff `verdict === "not_measured"`. */
  peak_ratio: number | null;
  verdict: "even" | "peaked" | "not_measured";
  not_measured_reason: string | null;
  term_id: string | null;
  term_buckets: ClockTermBucket[];
  /** How many of `slots` could be reported at all. Compare against `slots`,
   *  not against a "found" count: the thin buckets were never written by the
   *  rollup, so their number is not knowable and inventing one would be worse
   *  than saying so. */
  term_slots_measured: number;
  /** Set when a term was asked for and no ring can be drawn for THIS cycle and
   *  grain — the term rollup is bucketed by month and hour, so a weekday or a
   *  quarter-hour slot cannot be derived from it without inventing the slots it
   *  does not hold. Non-null with `term_id` set means "we cannot answer this
   *  combination", which is a different statement from an empty ring and must
   *  read differently on screen. The backend owns the sentence; never
   *  re-derive the condition here. */
  term_not_measured_reason: string | null;
}

export function useClock(
  orgSlug: string,
  days: number,
  cycle: ClockCycle,
  grain: ClockGrain,
  termId?: string,
): UseQueryResult<ClockReport, Error> {
  return useQuery<ClockReport, Error>({
    queryKey: ["analytics-clock", orgSlug, days, cycle, grain, termId ?? null],
    staleTime: STALE_MS,
    queryFn: async () => {
      const target = url(
        `/dashboards/${orgSlug}/clock`,
        termId ? { days, cycle, grain, term_id: termId } : { days, cycle, grain },
      );
      const res = await fetch(target, { headers: authHeaders() });
      if (!res.ok) throw new Error(`clock unavailable (${res.status})`);
      return (await res.json()) as ClockReport;
    },
  });
}
